/*
 * Author: Harper Yan
 * File Created: Friday, 24-07-19 02:32 pm
 * Last Modified: Tuesday, 25-04-15 02:13 pm, by Dw
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { produce } from 'immer';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { dateFormat, isNativeApp, toFixedFloor } from '@web/utils';

import type {
  CardFee,
  CardInfo,
  CardNetwork,
  CardTransactionDetail,
  CardTransactionsReq,
  CardTransactionSummary,
  CardType,
  EnhancedCardFee,
  MyCard,
  TransactionCard,
} from '@/@types/card';
import { SGD_CURRENCY_UNIT } from '@/constants';
import useLanguage from '@/hooks/useLanguage';
import useOpenApi from '@/hooks/useOpenApi';
import { getApplyFee } from '@/pages/card/_utils';
import {
  getCardBillPdfDownload,
  getCardBillPdfList,
  getCardFee,
  getCardList,
  getCardNameOptions,
  getCardTransactionDetail,
  getCardTransactionSummary,
  getCartTransactionDateRange,
  getMyCardDetail,
  getPhysicalApplicableCategories,
  getUserTransactionCardList,
} from '@/services/card/service';
import logger from '@/utils/logger';

import { useUserStore } from './useUserStore';

export interface BillList {
  year: string;
  months: string[];
}
export interface BillItem {
  expiredAt: number;
  billUrl: string;
}
const BILL_EXPIRED_TIME = 1000 * 60 * 4; // each link expired after 5min, but use 4min instead.

interface CardStoreState {
  cardList?: CardInfo[];
  nameOptionsOnCard?: string[];
  cardFee: Record<string, EnhancedCardFee | undefined>;
  cardDetail: Record<string, MyCard> | null;
  // currentCardDetail?: null | MyCard;
  activeSwiperCategoryId?: string;

  // 申请实体卡选择的列表和选中的 card 信息
  selectPhysicalCard?: CardInfo;
  physicalCardList?: CardInfo[];
  getPhysicalCardList: (_cardId: string) => Promise<CardInfo[]>;
  setSelectPhysicalCard: (_card: CardInfo) => void;

  clearCardInfo: () => void;
  monthyBills?: Record<string, BillList[]>;
  billUrls?: Record<string, BillItem>;
  setActiveSwiperCategoryId: (_categoryId: string) => void;
  getNameOptionsOnCard: (_cardId: string) => Promise<void>;
  getCardDetail: (_cardId: string) => Promise<void>;
  getCardFee: (_categoryId: string) => Promise<CardFee>;
  getMonthyBills: (_cardId: string) => Promise<BillList[] | undefined>;
  getCardList: (_p: { limits?: CardNetwork[] }) => Promise<CardInfo[]>;
  getBillUrl: (
    _cardId: string,
    _month: string,
  ) => Promise<BillItem | undefined>;
  // for card transactions new api
  transactionSummary: Record<string, CardTransactionSummary[]>;
  getTransactionSummary: (
    _params: CardTransactionsReq,
  ) => Promise<CardTransactionSummary[] | undefined>;
  transactionDateRange: Record<string, { year: string; months: string[] }[]>;
  getTransactionDateRange: (_params: {
    cardId?: string;
  }) => Promise<{ year: string; months: string[] }[]>;
  transactionDetail: Record<string, CardTransactionDetail[]>;
  getTransactionDetail: (_params: {
    page: number;
    rows: number;
    statementId: string;
    cardId?: string;
    force?: boolean;
  }) => Promise<CardTransactionDetail[]>;
  transactionCardList: Record<string, TransactionCard[]>;
  getTransactionCardList: (
    _locale: string,
  ) => Promise<TransactionCard[] | undefined>;
  clearTransactions: () => void;
}

export const useCardStore = create<CardStoreState>((set, get) => ({
  cardList: undefined,
  cardFee: {} as Record<CardType, EnhancedCardFee>,
  nameOptionsOnCard: undefined,
  // currentCardDetail: null,
  activeSwiperCategoryId: '',
  cardDetail: {} as Record<string, MyCard>,
  clearCardInfo: () => {
    set(() => ({
      cardDetail: {},
      physicalCardList: [],
      selectPhysicalCard: undefined,
      cardList: undefined,
    }));
  },
  // 申请实体卡选择的列表和选中的 card 信息
  selectPhysicalCard: undefined,
  physicalCardList: [],
  getPhysicalCardList: async (_cardId: string) => {
    const { data } = await getPhysicalApplicableCategories(_cardId);

    set(() => ({ physicalCardList: data }));

    return data;
  },
  setSelectPhysicalCard: (_card: CardInfo) => {
    set(() => ({ selectPhysicalCard: _card }));
  },

  monthyBills: undefined,
  getCardDetail: async (cardId) => {
    const { data } = await getMyCardDetail(cardId);

    set(
      produce((state) => {
        // state.currentCardDetail = data;
        state.cardDetail[cardId] = data;
      }),
    );
  },
  setActiveSwiperCategoryId: (categoryId) => {
    set(() => ({ activeSwiperCategoryId: categoryId }));
  },
  getNameOptionsOnCard: async (cardId) => {
    const { data } = await getCardNameOptions(cardId);

    set(() => ({ nameOptionsOnCard: data }));
  },
  getCardList: async (params: { limits?: CardNetwork[] }) => {
    const { limits } = params;
    const { data } = await getCardList();

    set(
      produce((state) => {
        state.cardList = data.filter((d: CardInfo) =>
          limits ? limits.includes(d.network) : true,
        );
        // state.cardDetail = {};
      }),
    );

    return data;
  },
  getCardFee: async (categoryId) => {
    const { data } = await getCardFee(categoryId);

    const newData = { ...data, unit: data.currency || SGD_CURRENCY_UNIT };

    set(
      produce((state) => {
        state.cardFee[categoryId] = newData;
      }),
    );

    return newData;
  },
  getMonthyBills: async (cardId: string) => {
    const { monthyBills } = get();

    if (monthyBills?.[cardId]) {
      return monthyBills[cardId];
    }

    const { data } = await getCardBillPdfList({ cardId });
    const bills: Record<string, string[]> = {};

    data.forEach((item) => {
      const [year, month] = item.split('-');

      if (typeof year === 'string' && typeof month === 'string') {
        if (!bills[year]) {
          bills[year] = [];
        }

        bills[year]!.push(month);
      }
    });
    const billsArr = Object.keys(bills)
      .map((year) => {
        return { year, months: bills[year]! };
      })
      .sort((a, b) => b.year.localeCompare(a.year));

    set(
      produce((state: CardStoreState) => {
        if (!state.monthyBills) state.monthyBills = {};
        state.monthyBills[cardId] = billsArr;
      }),
    );

    return billsArr;
  },
  getBillUrl: async (cardId: string, billMonth: string) => {
    if (!billMonth || !cardId) return;

    const { billUrls } = get();
    const key = `${cardId}@${billMonth}`;

    if (billUrls?.[key] && billUrls[key]!.expiredAt > Date.now()) {
      return billUrls[key];
    }

    const { data } = await getCardBillPdfDownload({ cardId, billMonth });
    const billItem = {
      expiredAt: Date.now() + BILL_EXPIRED_TIME,
      billUrl: data,
    };

    set(
      produce((state: CardStoreState) => {
        if (!state.billUrls) state.billUrls = {};
        state.billUrls[key] = billItem;
      }),
    );

    return billItem;
  },

  transactionSummary: {},
  getTransactionSummary: async (params: CardTransactionsReq) => {
    const { transactionSummary } = get();
    const key = `${params.page}_${params.rows}_${params.startTime ?? '*'}_${params.endTime ?? '*'}_${params.cardId || '*'}`;

    if (!params.force && transactionSummary[key]) {
      return transactionSummary[key];
    }

    const { data } = await getCardTransactionSummary(params);
    const _transData = data.map((item) => {
      const endDate = new Date(Number(item.statementDateStart));

      return {
        ...item,
        billMonth: dateFormat(endDate, 'YYYY-MM', true),
        month: (endDate.getMonth() + 1).toString(),
        paymentAmountInSgd: toFixedFloor(Number(item.paymentAmountInSgd), 2),
        paymentAmountInUsd: toFixedFloor(Number(item.paymentAmountInUsd), 2),
        nonPostedAmountInSgd: toFixedFloor(
          Number(item.nonPostedAmountInSgd),
          2,
        ),
        nonPostedAmountInUsd: toFixedFloor(
          Number(item.nonPostedAmountInUsd),
          2,
        ),
      };
    });

    set(
      produce((state: CardStoreState) => {
        state.transactionSummary[key] = _transData;
      }),
    );

    return _transData;
  },
  transactionDateRange: {},
  getTransactionDateRange: async ({ cardId }) => {
    const { transactionDateRange } = get();

    const key = cardId || '_';

    if (transactionDateRange[key]) {
      return transactionDateRange[key];
    }

    const generateYearMonthArray = (start: string, end: string) => {
      if (!/^\d{4}-\d{2}$/.test(start) || !/^\d{4}-\d{2}$/.test(end)) {
        return [];
      }

      const result: { year: string; months: string[] }[] = [];

      // 分别提取起始和结束的年份和月份
      const [startYear, startMonth] = start.split('-').map(Number);
      const [endYear, endMonth] = end.split('-').map(Number);

      if (!startYear || !startMonth || !endYear || !endMonth) return [];

      // 当前处理的年份和月份初始化为起始年月
      let currentYear = startYear;
      let currentMonth = startMonth;

      // 循环直到达到结束年月
      while (
        currentYear < endYear ||
        (currentYear === endYear && currentMonth <= endMonth)
      ) {
        // 查找当前年份是否已经存在于结果中
        const yearStr = currentYear.toString();
        let yearObj = result.find((item) => item.year === yearStr);

        if (!yearObj) {
          yearObj = { year: currentYear.toString(), months: [] };
          result.push(yearObj);
        }

        // 将月份添加到该年份的月份列表中
        yearObj.months.push(currentMonth.toString().padStart(2, '0'));

        // 增加月份，若超过12则重置为1并增加年份
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }

      return result.sort((a, b) => b.year.localeCompare(a.year));
    };

    try {
      const { data } = await getCartTransactionDateRange({ cardId });

      if (!data) {
        set(
          produce((state: CardStoreState) => {
            state.transactionDateRange[key] = [];
          }),
        );

        return [];
      }

      const MONTH_TS = 0; // 30 * 24 * 60 * 60 * 1000;

      const startDate = dateFormat(
        Number(data.start) - MONTH_TS,
        'YYYY-MM',
        true,
      );
      const endDate = dateFormat(Number(data.end) - MONTH_TS, 'YYYY-MM', true);
      // const endDate = dateFormat(Date.now(), 'YYYY-MM', true);
      const rangeData = generateYearMonthArray(startDate, endDate);

      set(
        produce((state: CardStoreState) => {
          state.transactionDateRange[key] = rangeData;
        }),
      );

      return rangeData;
    } catch (error) {
      set(
        produce((state: CardStoreState) => {
          state.transactionDateRange[key] = [];
        }),
      );

      return [];
    }
  },
  transactionDetail: {},

  getTransactionDetail: async (params: {
    page: number;
    rows: number;
    statementId: string;
    cardId?: string;
    force?: boolean;
  }) => {
    const { transactionDetail } = get();
    const key = `${params.page}_${params.rows}_${params.statementId}_${params.cardId || '*'}`;

    if (transactionDetail[key] && !params.force) {
      return transactionDetail[key];
    }

    const { data } = await getCardTransactionDetail(params);

    set(
      produce((state: CardStoreState) => {
        state.transactionDetail[key] = data;
      }),
    );

    return data;
  },
  transactionCardList: {},
  getTransactionCardList: async (locale: string) => {
    const { transactionCardList } = get();

    if (transactionCardList[locale]) {
      return transactionCardList[locale];
    }

    const { data } = await getUserTransactionCardList();

    set(
      produce((state: CardStoreState) => {
        state.transactionCardList[locale] = data;
      }),
    );

    return data;
  },
  clearTransactions: () => {
    set(() => ({
      transactionSummary: {},
      transactionDetail: {},
      transactionDateRange: {},
      transactionCardList: {},
    }));
  },
}));

const fetchRef = {
  isMyCardLoading: false,
  isSelectedCardLoading: false,
  isCardFeeLoading: false,
  isNameOptionsOnCardLoading: false,
};

export const useNameOnCardOptions = (cardId: string) => {
  const {
    cardList,
    nameOptionsOnCard,
    getNameOptionsOnCard: getNameOptions,
  } = useCardStore(
    useShallow((state) => ({
      cardList: state.cardList,
      nameOptionsOnCard: state.nameOptionsOnCard,
      getNameOptionsOnCard: state.getNameOptionsOnCard,
    })),
  );

  const [nameLoading, setNameLoading] = useState(false);

  const [cardOptionFetched, setCardOptionFetched] = useState(false);

  const fetchNames = useCallback(async () => {
    if (fetchRef.isNameOptionsOnCardLoading || cardOptionFetched) {
      return;
    }

    fetchRef.isNameOptionsOnCardLoading = true;
    setNameLoading(true);

    try {
      // todo
      await getNameOptions(cardId);
    } finally {
      fetchRef.isNameOptionsOnCardLoading = false;
      setNameLoading(false);
      setCardOptionFetched(true);
    }
  }, [getNameOptions, cardOptionFetched, cardId]);

  useEffect(() => {
    if (!nameLoading && !nameOptionsOnCard) {
      void fetchNames();
    }
  }, [cardList, fetchNames, nameLoading, nameOptionsOnCard]);

  return { cardList, nameOptionsOnCard };
};

export const useCardFee = (
  categoryId: string,
  options?: {
    isFirstApply?: boolean;
    isSelf?: boolean;
  },
) => {
  const { cardFee, getFee } = useCardStore(
    useShallow((state) => ({
      cardFee: state.cardFee,
      getFee: state.getCardFee,
    })),
  );
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchFee = useCallback(
    async (_categoryId: string) => {
      if (fetchRef.isCardFeeLoading) {
        return;
      }

      if (!_categoryId) {
        return;
      }

      fetchRef.isCardFeeLoading = true;
      setLoading(true);

      try {
        // todo1
        await getFee(_categoryId);
      } finally {
        fetchRef.isCardFeeLoading = false;
        setLoading(false);
        setFetched(true);
      }
    },
    [getFee],
  );

  useEffect(() => {
    if (!categoryId) {
      return;
    }

    if (!loading && !fetched && !cardFee[categoryId]) {
      void fetchFee(categoryId);
    }
  }, [fetchFee, fetched, categoryId, loading, cardFee]);

  const fee = cardFee[categoryId];

  if (!categoryId || !fee) {
    return { loading };
  }

  return { cardFee: getApplyFee(categoryId, fee, options), loading };
};

// 从 cardId  或者 categoryType 过滤？
// todo 这个是从cardList 中过滤的，后续应该不需要了？最好是移除
export const useSelectedCard = (params?: {
  cardId?: string;
  categoryId?: string;
}) => {
  const { cardList, getCards } = useCardStore(
    useShallow((state) => ({
      cardList: state.cardList,
      getCards: state.getCardList,
    })),
  );

  const { isOpenApiMode } = useOpenApi();
  const [isRetrieved, setIsRetrieved] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCards = useCallback(async () => {
    if (fetchRef.isSelectedCardLoading) {
      return;
    }

    try {
      fetchRef.isSelectedCardLoading = true;
      setLoading(true);
      await getCards({
        limits: isOpenApiMode && !isNativeApp() ? ['MASTERCARD'] : undefined,
      });
    } catch (error) {
      logger.error('Fail to fetchCards: ', error);
    }

    setIsRetrieved(true);
    fetchRef.isSelectedCardLoading = false;
    setLoading(false);
  }, [getCards, isOpenApiMode]);

  useEffect(() => {
    if (!loading && !isRetrieved && !cardList) {
      void fetchCards();
    }
  }, [cardList, fetchCards, isRetrieved, loading]);

  if (!params?.cardId && !params?.categoryId) {
    return { data: undefined, loading: true };
  }

  if (params.cardId) {
    return {
      data: cardList?.find((c) => c.cardId === params.cardId),
      loading,
    };
  }

  return {
    data: cardList?.find((c) => c.categoryId === params.categoryId),
    loading,
  };
};

export const usePhysicalCardList = (cardId: string, categoryId?: string) => {
  const { curLocale } = useLanguage();
  const {
    physicalCardList,
    getPhysicalCardList,
    setSelectPhysicalCard,
    selectPhysicalCard,
  } = useCardStore(
    useShallow((state) => ({
      physicalCardList: state.physicalCardList,
      getPhysicalCardList: state.getPhysicalCardList,
      setSelectPhysicalCard: state.setSelectPhysicalCard,
      selectPhysicalCard: state.selectPhysicalCard,
    })),
  );
  const [loading, setLoading] = useState(false);

  const fetchPhysicalCards = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await getPhysicalCardList(cardId);
    } catch (error) {
      logger.error('Fail to fetchCards: ', error);
    } finally {
      setLoading(false); // 设置 loading 为 false
    }
  }, [loading, getPhysicalCardList, cardId]);

  useEffect(() => {
    if (cardId) {
      void fetchPhysicalCards();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore
  }, [cardId, curLocale]);

  return {
    physicalCardList,
    loading,
    fetchPhysicalCards,
    setSelectPhysicalCard,
    selectPhysicalCard:
      selectPhysicalCard || categoryId
        ? physicalCardList?.find((item) => item.categoryId === categoryId)
        : null,
  };
};

export const useCardList = (
  params: {
    skipInitialFetch?: boolean;
    filterFn?: (_: CardInfo) => boolean;
  } = {
    skipInitialFetch: false,
  },
) => {
  const { curLocale } = useLanguage();

  const [loading, setLoading] = useState(false);

  const { isOpenApiMode } = useOpenApi();

  const {
    getCards,
    cardList,
    activeSwiperCategoryId,
    setActiveSwiperCategoryId,
  } = useCardStore(
    useShallow((state) => ({
      cardList: state.cardList,
      getCards: state.getCardList,
      activeSwiperCategoryId: state.activeSwiperCategoryId,
      setActiveSwiperCategoryId: state.setActiveSwiperCategoryId,
    })),
  );

  const fetchCards = useCallback(async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      const data = await getCards({
        limits: isOpenApiMode && !isNativeApp() ? ['MASTERCARD'] : undefined,
      });

      // if (data[0] && !activeSwiperCategoryId) {
      if (
        data[0] &&
        (!activeSwiperCategoryId ||
          !data.some((item) => item.categoryId === activeSwiperCategoryId))
      ) {
        setActiveSwiperCategoryId(data[0].categoryId);
      }
    } catch (error) {
      logger.error('Fail to fetchCards: ', error);
    } finally {
      setLoading(false); // 设置 loading 为 false
    }
  }, [
    loading,
    getCards,
    isOpenApiMode,
    activeSwiperCategoryId,
    setActiveSwiperCategoryId,
  ]);

  useEffect(() => {
    if (!loading && !params.skipInitialFetch) {
      void fetchCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore
  }, [curLocale, params.skipInitialFetch]);

  return {
    cardList: params.filterFn ? cardList?.filter(params.filterFn) : cardList,
    fetchCards,
    fetchCardListLoading: loading,
  };
};

export const useSwiperCard = (params?: Parameters<typeof useCardList>[0]) => {
  const { cardList, ...rest } = useCardList(params);
  const { activeSwiperCategoryId, setActiveSwiperCategoryId } = useCardStore(
    useShallow((state) => ({
      activeSwiperCategoryId: state.activeSwiperCategoryId,
      setActiveSwiperCategoryId: state.setActiveSwiperCategoryId,
    })),
  );

  return {
    cardList,
    ...rest,
    activeSwiperCategoryId,
    setActiveSwiperCategoryId,
    activeIndex:
      (cardList?.findIndex(
        (item) => item.categoryId === activeSwiperCategoryId,
      ) ?? -1) === -1
        ? 0
        : cardList?.findIndex(
            (item) => item.categoryId === activeSwiperCategoryId,
          ),
    currentSwiperCard: cardList?.find(
      (item) => item.categoryId === activeSwiperCategoryId,
    ),
  };
};

export const useCardDetail = (
  cardId: string,
  forceUpdate?: boolean,
  skipInitialFetch?: boolean,
) => {
  const { myCards, getMyCard } = useCardStore(
    useShallow((state) => ({
      myCards: state.cardDetail,
      getMyCard: state.getCardDetail,
    })),
  );

  const currentCardDetail = useMemo(() => {
    return (myCards?.[cardId] ?? {}) as MyCard;
  }, [myCards, cardId]);

  const isLogin = useUserStore((state) => state.isLogin);

  const [loading, setLoading] = useState(false);
  const [isRetrieved, setIsRetrieved] = useState(false);

  const requestId = useRef<string>('');

  const fetchMyCard = useCallback(
    async (id: string) => {
      if (fetchRef.isMyCardLoading) {
        return;
      }

      try {
        fetchRef.isMyCardLoading = true;
        setLoading(true);
        id && (await getMyCard(id));
      } catch (error) {
        logger.error('Fail to fetchMyCard: ', error);
      }

      setIsRetrieved(true);
      fetchRef.isMyCardLoading = false;
      setLoading(false);
    },
    [getMyCard],
  );

  useEffect(() => {
    if (cardId === requestId.current) return;

    if (requestId.current === '') {
      requestId.current = cardId;

      return;
    }

    requestId.current = cardId;
    fetchRef.isMyCardLoading = false;
    setIsRetrieved(false);
    setLoading(false);
  }, [cardId]);

  useEffect(() => {
    if (!cardId || skipInitialFetch) {
      return;
    }

    if (isLogin) {
      const myCard = myCards?.[cardId];
      const shouldFetch = !loading && !isRetrieved && (!myCard || forceUpdate);

      if (shouldFetch) {
        void fetchMyCard(cardId);
      }
    }
  }, [
    cardId,
    fetchMyCard,
    forceUpdate,
    isLogin,
    isRetrieved,
    loading,
    myCards,
    skipInitialFetch,
  ]);

  return { currentCardDetail, myCards, fetchMyCard, loading };
};

export const useCardVipApplyEnable = (
  card: MyCard | null | undefined,
  swiperCardId?: string,
) => {
  const { getPhysicalCardList } = useCardStore(
    useShallow((state) => ({
      getPhysicalCardList: state.getPhysicalCardList,
    })),
  );
  const [result, setResult] = useState<
    | {
        cardId: string;
        categoryId: string;
        type: string;
        reason: string;
        enable: boolean;
      }
    | undefined
  >();
  const [loading, setLoading] = useState(false);
  const defaultResult = useRef({
    cardId: '',
    categoryId: '',
    type: '',
    reason: '',
    enable: false,
  });
  const [physicalCardList, setPhysicalCardList] = useState<CardInfo[]>([]);

  const fetchPhysicalCardList = useCallback(async () => {
    if (loading || !card) return;

    if (
      // VIP卡
      card.cardType === 'VIP' ||
      // 非正常卡
      card.cardStatus !== 'NORMAL' ||
      // 非 master 卡都不会展示 升级新耀卡
      card.cardNetwork !== 'MASTERCARD'
    ) {
      setResult(defaultResult.current);

      return;
    }

    if (physicalCardList[0]?.cardId !== swiperCardId) {
      setResult(defaultResult.current);
    }

    setLoading(true);
    try {
      const list = await getPhysicalCardList(card.cardId ?? '');

      setPhysicalCardList(list);
      const vipCard = list.find((item) => item.type === 'VIP');

      if (vipCard) {
        const reason =
          card.physicalCardStatus !== 'UN_APPLY' ? 'ESCALATE' : 'FIRST_APPLY';

        setResult({
          cardId: card.cardId ?? '',
          categoryId: vipCard.categoryId,
          type: vipCard.type,
          reason,
          enable: true,
        });
      } else {
        setResult(defaultResult.current);
      }
    } catch (error) {
      setResult(defaultResult.current);
      logger.error('Fail to fetchPhysicalCardList: ', error);
    } finally {
      setLoading(false);
    }
  }, [loading, card, physicalCardList, swiperCardId, getPhysicalCardList]);

  useEffect(() => {
    if (card?.cardId) {
      void fetchPhysicalCardList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore
  }, [card?.cardId]);

  return { result, setResult, loading, physicalCardList };
};

export const useCardTransactionDateRange = (cardId: string) => {
  const { transactionDateRange, getTransactionDateRange } = useCardStore(
    useShallow((state) => ({
      transactionDateRange: state.transactionDateRange,
      getTransactionDateRange: state.getTransactionDateRange,
    })),
  );
  const [loading, setLoading] = useState(false);

  const fetchTransactionDateRange = useCallback(async () => {
    setLoading(true);
    try {
      await getTransactionDateRange({ cardId });
    } catch (error) {
      logger.error('Fail to fetchTransactionDateRange: ', error);
    }

    setLoading(false);
  }, [getTransactionDateRange, cardId]);

  useEffect(() => {
    if (cardId && !transactionDateRange[cardId] && !loading) {
      void fetchTransactionDateRange();
    }
  }, [loading, transactionDateRange, fetchTransactionDateRange, cardId]);

  return { transactionDateRange: transactionDateRange[cardId] };
};
